import { useTranslation } from 'react-i18next';
import { Tabs, TabPanel } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { REPORTS_TABS } from '@/Pages/tabRoutes';
import ReportsDashboard from './ReportsDashboard';
import ReportBuilderTab from './Builder/ReportBuilderTab';
import SchedulesTab from './Schedules/SchedulesTab';
import RunsTab from './Runs/RunsTab';
import ForecastingTab from './Forecasting/ForecastingTab';

/**
 * §21 / ADR-0013 — the Reports screen, now four tabs on one URL rather than a
 * second nav row.
 *
 * The existing fixed dashboard (`ReportsDashboard`, formerly this file) is
 * where §32's panels and the ad-hoc `QueryBuilder` already lived, gated on the
 * same `reports.view` this whole screen sits behind — the report builder this
 * epic adds is a way of composing and re-running one of those same queries
 * later, not a different capability, so it earns a tab here rather than a
 * glyph of its own on the rail.
 *
 * Only the open tab's panel is mounted (not merely hidden), so the dashboard's
 * half-dozen `/analytics/*` calls fire only while someone is actually looking
 * at it — switching to Builder does not keep polling KPIs nobody can see.
 */
export default function Reports() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/reports', REPORTS_TABS);

  if (!routed.open) return routed.redirect;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('nav.reports')}</h1>

      <Tabs
        idPrefix="reports"
        label={t('nav.reports')}
        value={routed.current}
        onChange={routed.select}
        tabs={routed.tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
      />

      <TabPanel idPrefix="reports" tab="dashboard" hidden={routed.current !== 'dashboard'}>
        {routed.current === 'dashboard' && <ReportsDashboard />}
      </TabPanel>
      <TabPanel idPrefix="reports" tab="builder" hidden={routed.current !== 'builder'}>
        {routed.current === 'builder' && <ReportBuilderTab />}
      </TabPanel>
      <TabPanel idPrefix="reports" tab="schedules" hidden={routed.current !== 'schedules'}>
        {routed.current === 'schedules' && <SchedulesTab />}
      </TabPanel>
      <TabPanel idPrefix="reports" tab="runs" hidden={routed.current !== 'runs'}>
        {routed.current === 'runs' && <RunsTab />}
      </TabPanel>
      <TabPanel idPrefix="reports" tab="forecasting" hidden={routed.current !== 'forecasting'}>
        {routed.current === 'forecasting' && <ForecastingTab />}
      </TabPanel>
    </div>
  );
}
